import React from 'react';
import { Box, Typography } from '@mui/material';

import ModalShell from './ModalShell';
import briefcaseIcon from '../../../Assets/Images/v3/icon-briefcase.svg';
import studentsIcon from '../../../Assets/Images/v3/icon-students.svg';
import arrowRightIcon from '../../../Assets/Images/v3/icon-arrow-right.svg';

export type ProductKey = 'assessment-service' | 'mycoach';

export interface ProductSelectionContentProps {
	onSelect: (product: ProductKey) => void;
}

interface ProductOption {
	key: ProductKey;
	icon: string;
	iconWidth: number;
	iconHeight: number;
	title: string;
	description: string;
}

const PRODUCT_OPTIONS: ProductOption[] = [
	{
		key: 'assessment-service',
		icon: briefcaseIcon,
		iconWidth: 26,
		iconHeight: 26.975,
		title: 'Assessment Service',
		description: 'Score & rank candidates from interviews'
	},
	{
		key: 'mycoach',
		icon: studentsIcon,
		iconWidth: 31.2,
		iconHeight: 31.2,
		title: 'MyCoach (L&D)',
		description: 'Leadership training & coaching'
	}
];

export const ProductSelectionContent = ({ onSelect }: ProductSelectionContentProps) => {
	return (
		<>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
				<Box
					sx={{
						display: 'inline-flex',
						alignSelf: 'flex-start',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '4px 6px',
						backgroundColor: '#e2edf8',
						borderRadius: '3px'
					}}
				>
					<Typography
						sx={{
							fontFamily: 'DM Mono',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							letterSpacing: '-0.28px',
							color: '#075ba9',
							opacity: 0.9
						}}
					>
						Let&apos;s talk
					</Typography>
				</Box>
				<Typography
					component="h2"
					sx={{
						margin: 0,
						fontFamily: 'DM Sans',
						fontWeight: 600,
						fontSize: '26px',
						lineHeight: 1.25,
						letterSpacing: '-0.78px',
						color: '#292929'
					}}
				>
					Which product can we help with?
				</Typography>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: '20px',
					width: '100%'
				}}
			>
				{PRODUCT_OPTIONS.map(option => (
					<Box
						key={option.key}
						component="button"
						type="button"
						onClick={() => onSelect(option.key)}
						sx={{
							flex: { sm: '1 1 0' },
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							gap: '43px',
							boxSizing: 'border-box',
							width: '100%',
							padding: '28px 26px',
							backgroundColor: '#ffffff',
							border: '1px solid #d8dfe5',
							borderRadius: '8px',
							cursor: 'pointer',
							textAlign: 'left',
							font: 'inherit',
							transition: 'border-color 0.15s ease',
							'&:hover': {
								borderColor: '#0073de'
							},
							'&:focus-visible': {
								outline: '2px solid #0073de',
								outlineOffset: '2px'
							}
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								gap: '25px',
								width: '100%'
							}}
						>
							<Box
								sx={{
									width: '52px',
									height: '52px',
									flexShrink: 0,
									borderRadius: '6.303px',
									backgroundColor: '#ffffff',
									border: '1px solid #0073de',
									boxShadow: '0 0 0 3.152px rgba(1, 133, 255, 0.28)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
							>
								<Box
									component="img"
									src={option.icon}
									alt=""
									sx={{ width: `${option.iconWidth}px`, height: `${option.iconHeight}px` }}
								/>
							</Box>

							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%' }}>
								<Typography
									sx={{
										margin: 0,
										fontFamily: 'DM Mono',
										fontWeight: 500,
										fontSize: '16px',
										lineHeight: 1.5,
										letterSpacing: '-0.32px',
										color: '#292929'
									}}
								>
									{option.title}
								</Typography>
								<Typography
									sx={{
										margin: 0,
										fontFamily: 'DM Mono',
										fontWeight: 400,
										fontSize: '14px',
										lineHeight: 1.5,
										letterSpacing: '-0.28px',
										color: '#6c6c6c'
									}}
								>
									{option.description}
								</Typography>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
							<Typography
								sx={{
									margin: 0,
									fontFamily: 'DM Mono',
									fontWeight: 500,
									fontSize: '16px',
									lineHeight: '24px',
									color: '#0073de'
								}}
							>
								Submit
							</Typography>
							<Box component="img" src={arrowRightIcon} alt="" sx={{ width: '16px', height: '16px' }} />
						</Box>
					</Box>
				))}
			</Box>
		</>
	);
};

export interface ProductSelectionModalProps {
	open: boolean;
	onClose: () => void;
	onSelect: (product: ProductKey) => void;
}

const ProductSelectionModal = ({ open, onClose, onSelect }: ProductSelectionModalProps) => {
	return (
		<ModalShell open={open} onClose={onClose}>
			<ProductSelectionContent onSelect={onSelect} />
		</ModalShell>
	);
};

export default ProductSelectionModal;
